/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import {
  AccessCodeV1,
  AvatarRecord,
  MicrotaskAssignmentRecord,
  Survey,
  TaskRecord,
  TaskSurveyRecord,
} from '@karya/core';
import { boxApi } from './Api';
import { useCallback, useLayoutEffect, useState } from 'react';
import { idb, models } from '../../db/Setup';

const V1_CONFIG = JSON.parse(import.meta.env.VITE_V1_ACCESS_CODE_CONFIG) as AccessCodeV1.Config;
const BOX_URLS = V1_CONFIG?.directs ?? [];

// Extract hooks from the box API
const {
  useLazyGetAvatarsWithTasksQuery,
  useLazyGetNextAvatarJourneyStateQuery,
  useGetAssignmentsMutation,
  useSubmitAssignmentMutation,
  useUpdateAvatarMutation,
  useRefuseTaskMutation,
  useSubmitSurveyMutation,
} = boxApi;

export type AvatarJourneyActions = {
  /** Update the avatar's worker-facing metadata. */
  uploadAvatarConfig: (avatar: AvatarRecord, config: AvatarRecord['config']) => Promise<AvatarRecord | null>;
  /** Update the avatar's worker-facing logs. */
  uploadAvatarLogs: (avatar: AvatarRecord, logs: AvatarRecord['logs']) => Promise<AvatarRecord | null>;
  /** Advance the avatar to the next journey state. */
  getNextJourneyStateRemote: (
    avatar: AvatarRecord,
    currentState?: AvatarRecord['journey_state'],
  ) => Promise<AvatarRecord | null>;
  /** Refuse the avatar's current task. */
  refuseTaskRemote: (avatar: AvatarRecord) => Promise<AvatarRecord | null>;
  /** Submit a survey response for the given task. */
  uploadAvatarSurvey: (avatar: AvatarRecord, taskId: string, survey: Survey) => Promise<TaskSurveyRecord | null>;
};

/**
 * Returns the public worker-journey actions used by the avatar flow.
 */
export function useAvatarJourneyActions(): AvatarJourneyActions {
  const [getNextAvatarJourneyState] = useLazyGetNextAvatarJourneyStateQuery();
  const [updateAvatar] = useUpdateAvatarMutation();
  const [refuseTask] = useRefuseTaskMutation();
  const [submitSurvey] = useSubmitSurveyMutation();

  const uploadAvatarRemote = useCallback(
    async (avatar: AvatarRecord, payload: { config?: AvatarRecord['config']; logs?: AvatarRecord['logs'] }) => {
      const [updatedAvatar, error] = await updateAvatar({
        access_code: avatar.access_code,
        config: payload.config,
        logs: payload.logs,
      })
        .unwrap()
        .intoResult()
        .toTuple();

      if (error) {
        console.error('Error updating avatar:', error.prettyPrint());
        return null;
      }

      await models.basic.upsertRecord('avatar', updatedAvatar);
      return updatedAvatar;
    },
    [updateAvatar],
  );

  const uploadAvatarConfig = useCallback(
    (avatar: AvatarRecord, config: AvatarRecord['config']) => {
      return uploadAvatarRemote(avatar, { config });
    },
    [uploadAvatarRemote],
  );

  const uploadAvatarLogs = useCallback(
    (avatar: AvatarRecord, logs: AvatarRecord['logs']) => {
      return uploadAvatarRemote(avatar, { logs });
    },
    [uploadAvatarRemote],
  );

  const getNextJourneyStateRemote = useCallback(
    async (avatar: AvatarRecord, currentState: AvatarRecord['journey_state'] = avatar.journey_state) => {
      const [updatedAvatar, error] = await getNextAvatarJourneyState({
        access_code: avatar.access_code,
        currentState,
      })
        .unwrap()
        .intoResult()
        .toTuple();

      if (error) {
        console.error('Error fetching next journey state for avatar:', error.prettyPrint());
        return null;
      }

      await models.basic.upsertRecord('avatar', updatedAvatar);
      return updatedAvatar;
    },
    [getNextAvatarJourneyState],
  );

  const refuseTaskRemote = useCallback(
    async (avatar: AvatarRecord) => {
      const [updatedAvatar, error] = await refuseTask({ access_code: avatar.access_code })
        .unwrap()
        .intoResult()
        .toTuple();

      if (error) {
        console.error('Error refusing task for avatar:', error.prettyPrint());
        return null;
      }

      await models.basic.upsertRecord('avatar', updatedAvatar);
      return updatedAvatar;
    },
    [refuseTask],
  );

  const uploadAvatarSurvey = useCallback(
    async (avatar: AvatarRecord, taskId: string, survey: Survey) => {
      const [surveyRecord, error] = await submitSurvey({
        access_code: avatar.access_code,
        task_id: taskId,
        survey,
      })
        .unwrap()
        .intoResult()
        .toTuple();

      if (error) {
        console.error('Error submitting survey for avatar:', error.prettyPrint());
        return null;
      }

      return surveyRecord;
    },
    [submitSurvey],
  );

  return { uploadAvatarConfig, uploadAvatarLogs, getNextJourneyStateRemote, refuseTaskRemote, uploadAvatarSurvey };
}

/**
 * Controller type for managing avatars and their associated tasks.
 */
export type AvatarsWithTaskController = {
  avatars: AvatarRecord[];
  tasks: TaskRecord[];
  dbError: Error | null;
  syncAvatarTasks: () => Promise<void>;
  isLoading?: boolean;
};

/** Hook to manage fetching and synchronizing avatars and their tasks.
 * @param workerId - The ID of the worker whose avatars are to be managed.
 * @return An object containing avatars, tasks, any database error, and
 * a function to synchronize avatar and task data with the box.
 * */
export function useGetAvatarsWithTask(workerId?: string): AvatarsWithTaskController {
  // Source of truth is the database
  const [avatarsInDb, setAvatarsInDb] = useState<AvatarRecord[]>([]);
  const [tasksInDb, setTasksInDb] = useState<TaskRecord[]>([]);
  const [dbError, setDbError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // API hook to fetch avatars with tasks from box
  const [getAvatarsWithTask] = useLazyGetAvatarsWithTasksQuery();

  /** Fetch avatars from the database for the given worker ID. */
  const fetchAvatarsFromDb = useCallback(async () => {
    const [avatars, error] = await models.basic
      .getAllFromIndex('avatar', 'worker_id', workerId)
      .unwrapResult()
      .toTuple();
    if (error) {
      console.error('Error fetching avatars from DB:', error.prettyPrint());
      setDbError(error);
      return [];
    }

    setAvatarsInDb(avatars);
    return avatars;
  }, [workerId]);

  /** Fetch tasks from the database that are associated with the avatars. */
  const fetchTasksFromDb = useCallback(async (avatars: AvatarRecord[]) => {
    const [allTasks, error] = await models.basic.getAll('task').unwrapResult().toTuple();
    if (error) {
      setDbError(error);
      return;
    }

    const taskIdsForAvatars = avatars.map((avatar) => avatar.task_id);
    const tasks = allTasks.filter((task) => {
      if (taskIdsForAvatars.includes(task.id)) {
        return true;
      }

      // Check if any avatars have the required tags for this task
      const hasRequiredTags = avatars?.some((avatar) => {
        // Avatar must have all the tags required by the task
        return task.itags.itags.every((tag) => avatar.tags.tags.includes(tag));
      });

      return hasRequiredTags;
    });

    setTasksInDb(tasks as TaskRecord[]);
  }, []);

  /** Set loading state with a delay to prevent flickering. */
  const delayedSetIsLoading = useCallback((value: boolean) => {
    setTimeout(() => setIsLoading(value), 1000);
  }, []);

  const syncAvatarTasks = useCallback(async () => {
    setIsLoading(true);
    for (const boxUrl of BOX_URLS) {
      const response = await getAvatarsWithTask({ baseUrl: boxUrl }).unwrap();
      const { avatars, tasks } = response;

      // Upsert avatars
      for (const avatar of avatars) {
        await models.basic.upsertRecord('avatar', avatar);
      }

      // Upsert tasks
      for (const task of tasks) {
        await models.basic.upsertRecord('task', task);
      }
    }

    // Refresh local state from the database
    await fetchAvatarsFromDb().then(fetchTasksFromDb);

    delayedSetIsLoading(false);
  }, [fetchAvatarsFromDb, fetchTasksFromDb, delayedSetIsLoading, getAvatarsWithTask]);

  // Synchronize avatars and tasks in the database with the box API.
  useLayoutEffect(() => {
    if (!workerId) return;
    setIsLoading(true);
    void fetchAvatarsFromDb()
      .then(fetchTasksFromDb)
      .then(() => delayedSetIsLoading(false));
  }, [delayedSetIsLoading, fetchAvatarsFromDb, fetchTasksFromDb, workerId]);

  return { avatars: avatarsInDb, tasks: tasksInDb, dbError, syncAvatarTasks, isLoading };
}

/**
 * Arguments for synchronizing an avatar's data with the box.
 */
export type SyncAvatarArgs = {
  avatar: AvatarRecord;
  updateJourneyState?: boolean;
  submitAssignments?: boolean;
  getNewAssignments?: boolean;
  getNewAssignmentsIfNoneExist?: boolean;
  getCompletedAssignments?: boolean;
  getVerifiedAssignments?: boolean;
};

/**
 * Function type for synchronizing an avatar's data with the box.
 */
export type SyncAvatarFunction = (args: SyncAvatarArgs) => Promise<void>;

/**
 * Hook to synchronize an avatar's journey state and assignments with the box.
 *
 * @return A function that takes SyncAvatarArgs and performs the synchronization.
 */
export function useSyncForAvatar(): SyncAvatarFunction {
  const [submitAssignment] = useSubmitAssignmentMutation();
  const [getAssignmentsForAvatar] = useGetAssignmentsMutation();
  const { getNextJourneyStateRemote } = useAvatarJourneyActions();

  /** Update the journey state of the given avatar by fetching the next state from the box. */
  const handleUpdateJourneyState = useCallback(
    async (avatar: AvatarRecord) => {
      await getNextJourneyStateRemote(avatar);
    },
    [getNextJourneyStateRemote],
  );

  /** Submit completed assignments for the given avatar to the box and update their status in the database. */
  const handleSubmitAssignments = useCallback(
    async (avatar: AvatarRecord) => {
      // Fetch completed assignments from the database
      const [avatarAssignments, getAvatarAssignmentsError] = await models.basic
        .getAllFromIndex('microtask_assignment', 'avatar_id', avatar.id)
        .unwrapResult()
        .toTuple();

      if (getAvatarAssignmentsError) {
        console.error('Error fetching assignments for avatar:', getAvatarAssignmentsError.prettyPrint());
        return;
      }

      const completedAssignments = avatarAssignments.filter((assignment) => assignment.status === 'COMPLETED');

      if (completedAssignments.length === 0) {
        return;
      }

      // Submit completed assignments to box
      const [_, submitError] = await submitAssignment({
        access_code: avatar.access_code,
        assignments: completedAssignments,
      })
        .unwrap()
        .intoResult()
        .toTuple();

      if (submitError) {
        console.error('Error submitting assignments for avatar:', submitError.prettyPrint());
        return;
      }

      for (const assignment of completedAssignments) {
        // Update assignment status to SUBMITTED in the database
        const updatedAssignment: MicrotaskAssignmentRecord = {
          ...assignment,
          status: 'SUBMITTED',
          submitted_to_box_at: new Date().toISOString(),
        };
        await models.basic.upsertRecord('microtask_assignment', updatedAssignment);
      }
    },
    [submitAssignment],
  );

  /** Fetch assignments of a specific type for the given avatar from the box and upsert them into the database. */
  const handleGetAssignments = useCallback(
    async (avatar: AvatarRecord, type: 'new' | 'completed' | 'verified') => {
      const typeToSortField = {
        new: 'created_at',
        completed: 'completed_at',
        verified: 'verified_at',
      } as const;

      const sortField = typeToSortField[type];
      const existingAssignments = await models.basic
        .getAllFromIndex('microtask_assignment', 'avatar_id', avatar.id)
        .unwrapResult()
        // Sort existing assignments by the relevant timestamp in descending order
        .map((assignments) =>
          assignments.sort((a, b) => {
            const dateA = new Date(a[sortField] || 0).getTime();
            const dateB = new Date(b[sortField] || 0).getTime();
            return dateB - dateA;
          }),
        )
        .getOr([]);

      const lastTimestamp = existingAssignments.at(0)?.[sortField] ?? undefined;
      const [getAssignmentsResponse, error] = await getAssignmentsForAvatar({
        type,
        access_code: avatar.access_code,
        from: lastTimestamp,
        limit: import.meta.env.VITE_ASSIGNMENTS_FETCH_LIMIT,
      })
        .unwrap()
        .intoResult()
        .toTuple();

      if (error) {
        console.error(`Error fetching ${type} assignments for avatar:`, error.prettyPrint());
        return;
      }

      const transaction = idb.transaction(['microtask', 'task', 'microtask_assignment'], 'readwrite');
      const microtaskStore = transaction.objectStore('microtask');
      const taskStore = transaction.objectStore('task');
      const assignmentStore = transaction.objectStore('microtask_assignment');

      const upsertResult = await Promise.all([
        // Upsert avatar
        Promise.all(getAssignmentsResponse.microtasks?.map((mt) => microtaskStore.put(mt)) ?? []),
        // Upsert tasks
        Promise.all(getAssignmentsResponse.tasks?.map((t) => taskStore.put(t)) ?? []),
        // Upsert assignments
        Promise.all(getAssignmentsResponse.assignments?.map((assn) => assignmentStore.put(assn)) ?? []),
      ]).intoResult();

      if (upsertResult.isErr()) {
        transaction.abort();
        console.error(`Error upserting ${type} assignments for avatar:`, upsertResult.error.prettyPrint());
        return;
      }

      await transaction.done.catch((txError) => {
        console.error(`Transaction error while upserting ${type} assignments for avatar:`, txError);
      });
    },
    [getAssignmentsForAvatar],
  );

  /** Main function to synchronize an avatar's data with the box based on provided arguments. */
  const handleSync = useCallback(
    async (args: SyncAvatarArgs) => {
      if (args.submitAssignments) {
        await handleSubmitAssignments(args.avatar);
      }

      const currentAssignmentCount = await models.basic
        .getCountFromIndex('microtask_assignment', 'avatar_id', args.avatar.id)
        .unwrapResult()
        .getOr(0);

      const shouldFetchNewAssignments =
        args.getNewAssignments || (args.getNewAssignmentsIfNoneExist && currentAssignmentCount === 0);

      // Concurrently fetch assignments of different types
      await Promise.allSettled([
        // Fetch new assignments if requested
        shouldFetchNewAssignments ? handleGetAssignments(args.avatar, 'new') : Promise.resolve(),
        // Fetch completed assignments if requested
        args.getCompletedAssignments ? handleGetAssignments(args.avatar, 'completed') : Promise.resolve(),
        // Fetch verified assignments if requested
        args.getVerifiedAssignments ? handleGetAssignments(args.avatar, 'verified') : Promise.resolve(),
      ]);

      // Switch journey state only after avatar-related data has been refreshed.
      if (args.updateJourneyState) {
        await handleUpdateJourneyState(args.avatar);
      }
    },
    [handleGetAssignments, handleSubmitAssignments, handleUpdateJourneyState],
  );

  return handleSync;
}
